// Platform IO boundary (save / serialize). The frontend owns schema + YAML
// serialization. The R2 commit output is a full per-camera estimations.toon:
// each loaded image frame is emitted under `frames`, with manual edits merged
// into pixel_coords/status. Local drafts still use the compact edit format.

import { Document, isSeq, visit } from 'yaml';
import { visibleKeypoints } from '../config/keypoints';
import { buildNewFormatOutput } from '../loaders/image/save';
import { useStore } from '../store';
import type { Side } from '../store/predictions';
import type { EditMap, NewPredictionsFile } from '../types';

const SIDE_TO_CAMERA: Record<Side, string> = {
  front_left: 'head_front_left',
  front_right: 'head_front_right',
  left: 'head_left',
  right: 'head_right',
};

function editsForSide(side: Side): EditMap {
  const s = useStore.getState();
  if (side === 'front_left') return s.editsFrontLeft;
  if (side === 'front_right') return s.editsFrontRight;
  if (side === 'left') return s.editsLeft;
  return s.editsRight;
}

export interface SerializedPose {
  toonText: string;
  frameCount: number;
}

function stringifyPoseYaml(value: Record<string, NewPredictionsFile>): string {
  const doc = new Document(value);
  visit(doc, (_key, node) => {
    if (isSeq(node)) node.flow = true;
  });
  return doc.toString({ flowCollectionPadding: false });
}

/** Build the adjusted estimations.toon text (full metadata + frames) from store state. */
export function buildAdjustedToon(): SerializedPose {
  const s = useStore.getState();
  const out: Record<string, NewPredictionsFile> = {};

  for (const side of Object.keys(SIDE_TO_CAMERA) as Side[]) {
    const raw = s.rawPredictions[side];
    if (!raw) continue;
    const camera = SIDE_TO_CAMERA[side];
    out[camera] = buildNewFormatOutput(raw, editsForSide(side), visibleKeypoints(side));
  }

  return {
    toonText: stringifyPoseYaml(out),
    frameCount: s.navigableFrames?.length ?? 0,
  };
}
